import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import {
  Button,
  Field,
  Input,
  Modal,
  RadioCardGroup,
  Textarea,
} from '@/components/ds';
import { captureContext } from '@/lib/feedback-capture';
import { useAuth } from '@/contexts/AuthContext';
import { useBranch } from '@/contexts/BranchContext';
import {
  feedbackApi,
  type CreateTicketPayload,
  type TicketType,
} from '@/api/feedback.api';
import { TYPE_META } from './ticket-vocabulary';
import { CapturedContextPanel } from './CapturedContextPanel';
import { AttachmentPicker, MAX_FILES } from './AttachmentPicker';

/**
 * The single submission flow.
 *
 * One entry point replaces the two sidebar items that both wrote the same
 * record type — the kind of report is chosen here, in the form, rather than
 * decided by which link somebody happened to click. The type comes first
 * because it changes the questions: a broken page needs "what did you expect",
 * a wrong figure needs the two numbers side by side, and a feature request
 * needs neither.
 */
export function SubmitTicketModal({
  isOpen,
  onClose,
  onSubmitted,
  initialType = 'bug',
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: (ticketId: number) => void;
  initialType?: TicketType;
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();

  const [type, setType] = useState<TicketType>(initialType);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [expected, setExpected] = useState('');
  const [steps, setSteps] = useState('');
  const [figureLabel, setFigureLabel] = useState('');
  const [shownValue, setShownValue] = useState('');
  const [expectedValue, setExpectedValue] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<number | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  /**
   * Captured once when the modal opens, not on every render.
   *
   * The user is shown this exact object and then it is what gets sent —
   * re-reading at submit time could attach a different page's state than the
   * one they agreed to.
   */
  const context = useMemo(
    () =>
      isOpen
        ? captureContext({
            role: user?.clinicRole,
            language: i18n.language,
          })
        : {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen],
  );

  const dirty =
    title.trim() !== '' ||
    description.trim() !== '' ||
    expected.trim() !== '' ||
    steps.trim() !== '' ||
    figureLabel.trim() !== '' ||
    files.length > 0;

  const reset = () => {
    setType(initialType);
    setTitle('');
    setDescription('');
    setExpected('');
    setSteps('');
    setFigureLabel('');
    setShownValue('');
    setExpectedValue('');
    setFiles([]);
    setError(null);
    setStatus('');
    setSubmittedId(null);
    setConfirmDiscard(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  /**
   * Guard the exit when there is something to lose.
   *
   * The previous modal dismissed on a backdrop click and discarded whatever had
   * been typed — the fastest way to make someone give up on reporting a bug
   * twice.
   */
  const requestClose = () => {
    if (submittedId !== null || !dirty) {
      close();
      return;
    }
    setConfirmDiscard(true);
  };

  const canSubmit =
    title.trim().length > 0 &&
    (type === 'incorrect_data'
      ? figureLabel.trim().length > 0
      : description.trim().length > 0);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload: CreateTicketPayload = {
        type,
        title: title.trim(),
        description:
          description.trim() ||
          // An incorrect-data report is carried by its structured fields; the
          // free-text box is genuinely optional there, but the column is not.
          `${figureLabel.trim()}: ${shownValue.trim()} → ${expectedValue.trim()}`,
        expectedBehaviour: expected.trim() || undefined,
        stepsToReproduce: steps.trim() || undefined,
        incorrectData:
          type === 'incorrect_data'
            ? {
                figureLabel: figureLabel.trim() || undefined,
                shownValue: shownValue.trim() || undefined,
                expectedValue: expectedValue.trim() || undefined,
              }
            : undefined,
        branchId: selectedBranch?.id,
        context,
      };

      const ticket = await feedbackApi.create(payload);

      // Files upload after the ticket exists, so a failed upload leaves a
      // usable report rather than losing the whole submission. Failures are
      // surfaced but do not discard what was written.
      if (files.length > 0) {
        setStatus(t('feedback.attachments.uploading'));
        for (const file of files.slice(0, MAX_FILES)) {
          try {
            await feedbackApi.attach(ticket.id, file, { label: file.name });
          } catch {
            setError(t('feedback.attachments.failed'));
          }
        }
      }

      setSubmittedId(ticket.id);
      onSubmitted?.(ticket.id);
    } catch {
      setError(t('feedback.errors.submitFailed'));
    } finally {
      setSubmitting(false);
      setStatus('');
    }
  };

  /* ── Confirmation ─────────────────────────────────────── */

  if (submittedId !== null) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={close}
        title={t('feedback.confirmation.title')}
        icon={<CheckCircle2 className="size-5 text-success-600" />}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onPress={close}>
              {t('feedback.confirmation.done')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-body text-foreground">
            {t('feedback.confirmation.body')}
          </p>
          {/* An expectation, not a guarantee — see the SLA note in the audit. */}
          <p className="text-small text-muted">
            {t('feedback.confirmation.sla')}
          </p>
        </div>
      </Modal>
    );
  }

  /* ── Form ─────────────────────────────────────────────── */

  return (
    <>
      <Modal
        isOpen={isOpen && !confirmDiscard}
        onClose={requestClose}
        title={t('feedback.form.title')}
        size="4xl"
        isDismissable={!dirty}
        footer={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            {status && (
              <span className="text-small text-muted sm:me-auto">{status}</span>
            )}
            <Button
              variant="secondary"
              onPress={requestClose}
              isDisabled={submitting}
            >
              {t('feedback.form.cancel')}
            </Button>
            <Button
              onPress={submit}
              isDisabled={!canSubmit || submitting}
              isLoading={submitting}
              loadingText={t('feedback.form.submitting')}
            >
              {t('feedback.form.submit')}
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          <RadioCardGroup<TicketType>
            label={t('feedback.form.typeQuestion')}
            value={type}
            onChange={setType}
            columns={3}
            options={(Object.keys(TYPE_META) as TicketType[]).map((key) => {
              const meta = TYPE_META[key];
              const Icon = meta.icon;
              return {
                value: key,
                label: t(meta.labelKey),
                description: t(meta.descriptionKey),
                icon: <Icon className="size-4" />,
              };
            })}
          />

          <Field label={t('feedback.form.titleLabel')} required>
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={t('feedback.form.titlePlaceholder')}
              maxLength={255}
            />
          </Field>

          {type === 'incorrect_data' ? (
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label={t('feedback.form.incorrectData.figure')} required>
                <Input
                  value={figureLabel}
                  onChange={(event) => setFigureLabel(event.target.value)}
                  placeholder={t(
                    'feedback.form.incorrectData.figurePlaceholder',
                  )}
                />
              </Field>
              <Field label={t('feedback.form.incorrectData.shown')}>
                <Input
                  value={shownValue}
                  onChange={(event) => setShownValue(event.target.value)}
                  placeholder={t(
                    'feedback.form.incorrectData.shownPlaceholder',
                  )}
                />
              </Field>
              <Field label={t('feedback.form.incorrectData.expectedValue')}>
                <Input
                  value={expectedValue}
                  onChange={(event) => setExpectedValue(event.target.value)}
                  placeholder={t(
                    'feedback.form.incorrectData.expectedValuePlaceholder',
                  )}
                />
              </Field>
              <Field
                label={t('feedback.form.incorrectData.description')}
                className="sm:col-span-3"
              >
                <Textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder={t(
                    'feedback.form.incorrectData.descriptionPlaceholder',
                  )}
                  rows={3}
                />
              </Field>
            </div>
          ) : (
            // One row of three on desktop: stacked, the form ran 120px past a
            // 1366×768 screen and scrolled inside the dialog.
            <div
              className={
                type === 'bug'
                  ? 'grid gap-4 sm:grid-cols-3'
                  : 'grid gap-4 sm:grid-cols-2'
              }
            >
              <Field
                label={t(
                  type === 'bug'
                    ? 'feedback.form.bug.description'
                    : 'feedback.form.feature.description',
                )}
                required
              >
                <Textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder={t(
                    type === 'bug'
                      ? 'feedback.form.bug.descriptionPlaceholder'
                      : 'feedback.form.feature.descriptionPlaceholder',
                  )}
                  rows={3}
                />
              </Field>

              <Field
                label={t(
                  type === 'bug'
                    ? 'feedback.form.bug.expected'
                    : 'feedback.form.feature.expected',
                )}
              >
                <Textarea
                  value={expected}
                  onChange={(event) => setExpected(event.target.value)}
                  placeholder={t(
                    type === 'bug'
                      ? 'feedback.form.bug.expectedPlaceholder'
                      : 'feedback.form.feature.expectedPlaceholder',
                  )}
                  rows={3}
                />
              </Field>

              {type === 'bug' && (
                <Field label={t('feedback.form.bug.steps')}>
                  <Textarea
                    value={steps}
                    onChange={(event) => setSteps(event.target.value)}
                    placeholder={t('feedback.form.bug.stepsPlaceholder')}
                    rows={3}
                  />
                </Field>
              )}
            </div>
          )}

          <AttachmentPicker
            files={files}
            onChange={setFiles}
            onReject={setError}
            disabled={submitting}
          />

          <CapturedContextPanel context={context} />

          {error && (
            <p role="alert" className="text-small text-danger-600">
              {error}
            </p>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        title={t('feedback.form.discardTitle')}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onPress={() => setConfirmDiscard(false)}
            >
              {t('feedback.form.discardCancel')}
            </Button>
            <Button variant="danger" onPress={close}>
              {t('feedback.form.discardConfirm')}
            </Button>
          </div>
        }
      >
        <p className="text-body text-foreground">
          {t('feedback.form.discardBody')}
        </p>
      </Modal>
    </>
  );
}
